import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CategoryRecord } from '../../catalog/domain-api'
import { listCategories } from '../../catalog/infra-api'
import { listActiveSuppliersLocal } from '../../catalog/infra-api'
import { listDistinctProductBrandsLocal } from '../../catalog/infra-api'
import { buildCategoryFilterOptions, buildCategoryOptions } from '../../catalog/domain-api';
import { isDatabaseUnavailableError } from '../../shared/localDb';
import { subscribeCatalogSynced } from '../../sync/core/catalogSyncListeners';

export const useInventoryMasterData = () => {
  const [categories, setCategories] = useState<CategoryRecord[]>(() => []);
  const [stockBrandOptions, setStockBrandOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [stockSupplierOptions, setStockSupplierOptions] = useState<Array<{ id: string; name: string }>>([]);

  const refreshMasterData = useCallback(async () => {
    try {
      const localRows = await listCategories();
      setCategories(localRows.length > 0 ? localRows : []);
    } catch (error) {
      if (!isDatabaseUnavailableError(error)) {
        console.warn('[Inventory] No se pudieron leer categorías locales', error);
      }
    }
    try {
      const [brands, suppliers] = await Promise.all([
        listDistinctProductBrandsLocal(),
        listActiveSuppliersLocal(),
      ]);
      setStockBrandOptions(brands);
      setStockSupplierOptions(suppliers);
    } catch (error) {
      console.warn('[Inventory] Error al recargar marcas/proveedores:', error);
    }
  }, []);

  useEffect(() => {
    void refreshMasterData();
    const unsubscribeCatalogSynced = subscribeCatalogSynced(() => {
      void refreshMasterData().catch((error) => {
        console.warn('[Inventory] Error al recargar datos después de sync:', error);
      });
    });
    return unsubscribeCatalogSynced;
  }, [refreshMasterData]);

  const bulkCategoryOptions = useMemo(() => {
    return buildCategoryOptions(categories, {
      fallbackCategories: [],
      forItemType: 'physical',
    });
  }, [categories]);

  const inventoryStockCategoryFilters = useMemo(() => {
    return buildCategoryFilterOptions(categories, {
      allLabel: 'Todas',
      fallbackCategories: [],
      forItemType: 'physical',
    });
  }, [categories]);

  return {
    categories,
    stockBrandOptions,
    stockSupplierOptions,
    bulkCategoryOptions,
    inventoryStockCategoryFilters,
  };
};
